import {
  createEmptySubCapability,
  emptyAssessmentFields,
  normalizeAssessmentFieldsFromApi,
  normalizeSubCapabilityFromApi,
  serializeMaturityOptions,
  withAddedMaturityOption,
  withCustomMaturityToggle,
  withMaturityOptionPatch,
  withRemovedMaturityOption,
} from "../../../utils/subCapabilityAssessmentConfig";
import { withQuestionList } from "../../../utils/subCapabilityQuestionUtils";

export const CAPABILITY_CATALOG_PATH = "/sa/capability_management";

export const capabilityCreatePath = () => `${CAPABILITY_CATALOG_PATH}/new`;

export const capabilityEditPath = (capabilityId: number) =>
  `${CAPABILITY_CATALOG_PATH}/${capabilityId}/edit`;

export const EMPTY_CAPABILITY_FORM = {
  id: 0,
  businessTypeId: "" as "" | number,
  frameworkId: "" as "" | number,
  capabilityName: "",
  capabilityIconUrl: "",
  capabilityDescription: "",
  isActive: true,
  currentStateQuestions: [] as unknown[],
  futureStateQuestions: [] as unknown[],
  ...emptyAssessmentFields(),
  subCapabilities: [] as ReturnType<typeof createEmptySubCapability>[],
};

export type CapabilityFormData = typeof EMPTY_CAPABILITY_FORM;

export const hasNamedSubCapabilities = (subs: CapabilityFormData["subCapabilities"] = []) =>
  (subs || []).some((sub) => String(sub?.subCapabilityName || (sub as { name?: string })?.name || "").trim());

export const mapApiCapabilityToFormData = (
  capability: Record<string, unknown>
): CapabilityFormData => {
  const capabilityId = Number(
    capability.id || capability.capabilityId || capability.ID || 0
  );
  return {
    id: capabilityId,
    businessTypeId: (capability.businessTypeId as number) || "",
    frameworkId: (capability.frameworkId as number) || "",
    capabilityName: String(capability.capabilityName || ""),
    capabilityIconUrl: String(capability.capabilityIconUrl || ""),
    capabilityDescription: String(capability.capabilityDescription || ""),
    isActive: capability.isActive !== undefined ? Boolean(capability.isActive) : true,
    currentStateQuestions: (capability.currentStateQuestions as unknown[]) || [],
    futureStateQuestions: (capability.futureStateQuestions as unknown[]) || [],
    ...normalizeAssessmentFieldsFromApi(capability),
    subCapabilities: ((capability.subCapabilities as unknown[]) || []).map((sub) =>
      normalizeSubCapabilityFromApi(sub as Record<string, unknown>, capabilityId)
    ),
  };
};

export const createEmptyCapabilityFormData = (
  businessTypes: Array<{ id: number; name?: string }>
): CapabilityFormData => {
  const itType = businessTypes.find((bt) => String(bt.name || "").toLowerCase() === "it");
  return {
    ...EMPTY_CAPABILITY_FORM,
    ...emptyAssessmentFields(),
    businessTypeId: itType?.id != null ? itType.id : "",
    subCapabilities: [],
  };
};

export const buildCapabilitySavePayload = (formData: CapabilityFormData) => ({
  ...formData,
  maturityOptionsJson: hasNamedSubCapabilities(formData.subCapabilities)
    ? null
    : serializeMaturityOptions(formData.maturityOptions, Boolean(formData.useCustomMaturity)),
  currentStateCommentPlaceholder: hasNamedSubCapabilities(formData.subCapabilities)
    ? null
    : formData.currentStateCommentPlaceholder,
  futureStateCommentPlaceholder: hasNamedSubCapabilities(formData.subCapabilities)
    ? null
    : formData.futureStateCommentPlaceholder,
  subCapabilities: formData.subCapabilities.map((sub) => ({
    ...sub,
    maturityOptionsJson: serializeMaturityOptions(
      sub.maturityOptions,
      Boolean(sub.useCustomMaturity)
    ),
  })),
});

export {
  createEmptySubCapability,
  withQuestionList,
  withCustomMaturityToggle,
  withMaturityOptionPatch,
  withAddedMaturityOption,
  withRemovedMaturityOption,
};
